import { useEffect, useCallback, useRef, type ReactNode } from 'react';
import { IconButton } from 'shared/components/IconButton';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  titleId?: string;
  children: ReactNode;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ isOpen, onClose, titleId, children }: ModalProps) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab' && modalContentRef.current) {
        const focusable = modalContentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Focus first focusable element inside modal
      requestAnimationFrame(() => {
        const firstFocusable = modalContentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      });
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';

      if (!isOpen && previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-surface-container-lowest/80 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        ref={modalContentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-2xl bg-surface-container-low rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-tertiary/20 overflow-hidden"
      >
        {/* Decorative gold gradient line */}
        <div className="h-1 bg-gradient-to-r from-transparent via-tertiary/40 to-transparent w-full" />

        <div className="p-8 lg:p-12">
          <IconButton
            variant="link"
            size="lg"
            icon="x"
            onClick={onClose}
            className="absolute top-8 right-8 text-outline hover:text-on-surface"
            aria-label="Close modal"
          />
          {children}
        </div>

        {/* Decorative footer gradient */}
        <div className="absolute bottom-0 left-0 w-full h-24 pointer-events-none opacity-10">
          <div className="bg-gradient-to-t from-tertiary/20 to-transparent h-full w-full" />
        </div>
      </div>
    </div>
  );
}
